// Absences (admin). One row per SPELL rather than per day, so a fortnight's holiday is one thing
// to record, approve and report on.
//
// The two columns that did not exist before are the point of this screen:
//   Notified  — did they tell us, when, and how. Notice is shown in hours; NEGATIVE means we were
//               told after the day had already started. An absence nobody reported is a no-show.
//   Certif.   — sick leave needs a document, so missing ones can be chased instead of forgotten.
import React, { useCallback, useEffect, useState } from 'react';
import { authFetch, API } from './api';
import HelpPanel from './HelpPanel';

interface Absence {
  id: number; username: string; display_name: string; type: string; type_label: string;
  start_date: string; end_date: string; half_day_start: boolean; half_day_end: boolean;
  notified: boolean | null; notified_at: string | null; notified_via: string | null;
  notice_hours: number | null;
  certificate_required: boolean; certificate_received: boolean;
  certificate_date: string | null; certificate_ref: string | null;
  status: string; note: string | null; paid: boolean | null; no_show: boolean;
  created_by: string;
}
interface Person { username: string; display_name: string }

const inp = 'bg-gray-800 border border-gray-700 text-gray-200 text-xs rounded px-2 py-1 focus:outline-none focus:border-blue-500';
const btn = 'px-2.5 py-1 text-xs rounded-lg';

const VIA = [['', '—'], ['phone', 'Phone'], ['message', 'Message'], ['in_person', 'In person'],
             ['email', 'Email'], ['other', 'Other']] as const;

const noticeLabel = (h: number | null) => {
  if (h === null || h === undefined) return '—';
  if (h < 0) return `${Math.abs(Math.round(h))}h late`;
  if (h < 48) return `${Math.round(h)}h ahead`;
  return `${Math.round(h / 24)}d ahead`;
};

export default function AbsencesPanel() {
  const [rows, setRows] = useState<Absence[]>([]);
  const [types, setTypes] = useState<Record<string, string>>({});
  const [people, setPeople] = useState<Person[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const today = new Date();
  const [from, setFrom] = useState(new Date(today.getFullYear(), today.getMonth() - 1, 1).toISOString().slice(0, 10));
  const [to, setTo] = useState(new Date(today.getFullYear(), today.getMonth() + 2, 0).toISOString().slice(0, 10));
  const [who, setWho] = useState('');
  const [onlyNoShow, setOnlyNoShow] = useState(false);
  const [onlyCert, setOnlyCert] = useState(false);

  const [editing, setEditing] = useState<Absence | 'new' | null>(null);
  const [lastResult, setLastResult] = useState<string | null>(null);

  const load = useCallback(async () => {
    setErr(null);
    const q = new URLSearchParams({ from_date: from, to_date: to });
    if (who) q.set('username', who);
    if (onlyNoShow) q.set('no_shows', 'true');
    if (onlyCert) q.set('open_certificates', 'true');
    const r = await authFetch(`${API}/statistics/absences?${q}`);
    if (!r.ok) { setErr('Could not load absences'); return; }
    const d = await r.json();
    setRows(d.absences || []);
    setTypes(d.types || {});
  }, [from, to, who, onlyNoShow, onlyCert]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    authFetch(`${API}/statistics/employment`).then(r => r.ok ? r.json() : null).then(d => {
      if (d) setPeople((d.people || []).map((p: any) => ({ username: p.username, display_name: p.display_name })));
    });
  }, []);

  const call = async (url: string, method: string, body?: any) => {
    setBusy(true); setErr(null);
    const r = await authFetch(url, {
      method, headers: { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    setBusy(false);
    if (!r.ok) {
      let m = `${r.status}`;
      try { m = (await r.json()).detail || m; } catch { /* keep status */ }
      setErr(m); return null;
    }
    const out = await r.json().catch(() => ({}));
    await load();
    return out;
  };

  const markCert = (a: Absence) =>
    call(`${API}/statistics/absences/${a.id}/certificate`, 'POST',
         { received: !a.certificate_received });

  const remove = async (a: Absence) => {
    if (!window.confirm(`Remove ${a.type_label} for ${a.display_name} (${a.start_date}…${a.end_date})?\n\nThe calendar days and any vacation booking go with it. The audit record stays.`)) return;
    await call(`${API}/statistics/absences/${a.id}`, 'DELETE');
  };

  return (
    <div>
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <label className="text-xs text-gray-500">From
          <input type="date" className={`${inp} ml-2`} value={from} onChange={e => setFrom(e.target.value)} />
        </label>
        <label className="text-xs text-gray-500">To
          <input type="date" className={`${inp} ml-2`} value={to} onChange={e => setTo(e.target.value)} />
        </label>
        <label className="text-xs text-gray-500">Person
          <select className={`${inp} ml-2`} value={who} onChange={e => setWho(e.target.value)}>
            <option value="">Everyone</option>
            {people.map(p => <option key={p.username} value={p.username}>{p.display_name}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-2 text-xs text-gray-400">
          <input type="checkbox" checked={onlyNoShow} onChange={e => setOnlyNoShow(e.target.checked)} />
          No-shows only
        </label>
        <label className="flex items-center gap-2 text-xs text-gray-400">
          <input type="checkbox" checked={onlyCert} onChange={e => setOnlyCert(e.target.checked)} />
          Awaiting certificate
        </label>
        <button onClick={() => setEditing('new')}
          className="ml-auto px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-lg font-medium">
          + Record absence
        </button>
      </div>

      {err && (
        <div className="mb-3 bg-red-950/40 border border-red-900/60 text-red-300 text-xs rounded-lg px-3 py-2">{err}</div>
      )}
      {lastResult && (
        <div className="mb-3 bg-[#1a1d27] border border-gray-800 text-xs text-gray-400 rounded-lg px-3 py-2">{lastResult}</div>
      )}

      <div className="bg-[#1a1d27] rounded-xl border border-gray-800 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-gray-500 text-xs border-b border-gray-800">
              <th className="px-3 py-2 text-left font-medium">Person</th>
              <th className="px-3 py-2 text-left font-medium">Type</th>
              <th className="px-3 py-2 text-left font-medium">Dates</th>
              <th className="px-3 py-2 text-left font-medium">Notified</th>
              <th className="px-3 py-2 text-left font-medium">Certificate</th>
              <th className="px-3 py-2 text-left font-medium">Note</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-gray-600 text-sm">
                No absences match these filters.
              </td></tr>
            )}
            {rows.map(a => (
              <tr key={a.id} className="border-b border-gray-800/50">
                <td className="px-3 py-2.5 text-gray-200">{a.display_name}</td>
                <td className="px-3 py-2.5">
                  <span className={`text-[11px] px-2 py-0.5 rounded ${a.paid ? 'bg-blue-900/50 text-blue-300' : 'bg-gray-800 text-gray-400'}`}>
                    {a.type_label}
                  </span>
                  {a.paid === false && <span className="ml-1 text-[10px] text-gray-600">unpaid</span>}
                </td>
                <td className="px-3 py-2.5 text-xs text-gray-400 whitespace-nowrap">
                  {a.start_date}{a.end_date !== a.start_date && ` → ${a.end_date}`}
                  {(a.half_day_start || a.half_day_end) && (
                    <span className="ml-1 text-[10px] text-amber-500/80">½</span>
                  )}
                </td>
                <td className="px-3 py-2.5 text-xs">
                  {a.no_show ? (
                    <span className="text-red-400 font-medium" title="Nobody told us — a no-show">no-show</span>
                  ) : a.notified === true ? (
                    <span className="text-gray-300">
                      yes
                      <span className={`ml-1 ${(a.notice_hours ?? 0) < 0 ? 'text-amber-400' : 'text-gray-500'}`}>
                        {noticeLabel(a.notice_hours)}
                      </span>
                      {a.notified_via && <span className="text-gray-600"> · {a.notified_via}</span>}
                    </span>
                  ) : a.notified === false ? (
                    <span className="text-amber-400">not notified</span>
                  ) : <span className="text-gray-700">—</span>}
                </td>
                <td className="px-3 py-2.5 text-xs">
                  {!a.certificate_required ? <span className="text-gray-700">n/a</span>
                    : a.certificate_received ? (
                      <span className="text-green-400">
                        received{a.certificate_ref ? ` · ${a.certificate_ref}` : ''}
                      </span>
                    ) : <span className="text-amber-400 font-medium">outstanding</span>}
                </td>
                <td className="px-3 py-2.5 text-xs text-gray-500 max-w-[220px] truncate" title={a.note || ''}>
                  {a.note || ''}
                </td>
                <td className="px-3 py-2.5 text-right whitespace-nowrap">
                  {a.certificate_required && (
                    <button onClick={() => markCert(a)} disabled={busy}
                      className={`${btn} bg-gray-800 hover:bg-gray-700 text-gray-300 mr-1.5`}>
                      {a.certificate_received ? 'Un-receive' : 'Got it'}
                    </button>
                  )}
                  <button onClick={() => setEditing(a)}
                    className={`${btn} bg-gray-800 hover:bg-gray-700 text-gray-300 mr-1.5`}>Edit</button>
                  <button onClick={() => remove(a)} disabled={busy}
                    className={`${btn} bg-gray-800 hover:bg-red-900/60 text-gray-400 hover:text-red-300`}>Remove</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <AbsenceDrawer absence={editing === 'new' ? null : editing} types={types} people={people}
          busy={busy} onClose={() => setEditing(null)}
          onSaved={msg => { setLastResult(msg); setEditing(null); }}
          call={call} />
      )}

      <HelpPanel items={[
        { label: 'Record absence', desc: 'Creates one spell covering a date range. It writes itself onto the calendar, skipping weekends, public holidays, days outside their employment and days already covered by another absence — and it tells you which it skipped.' },
        { label: 'Notified', desc: 'Whether they told us, and how far in advance. "12h late" means we were told after the day had already started. An absence with nobody notified is flagged as a no-show — that is the difference between someone who phoned in sick and someone who simply did not appear.' },
        { label: 'No-shows only', desc: 'Filters to unreported absences. Useful for spotting a pattern of someone not calling in.' },
        { label: 'Certificate / Awaiting certificate', desc: 'Sick leave (haigusleht) needs a document. Outstanding ones stay flagged so they can be chased; "Got it" records the reference and clears the flag.' },
        { label: 'Half day (½)', desc: 'A half-day credits half the shift and expects the rest to be worked, so a doctor’s appointment does not cost a whole day of pay or a whole day of vacation.' },
        { label: 'Type — paid vs unpaid', desc: 'Paid types (vacation, certified sick, child/study/bereavement leave) credit the day. Unpaid leave is a shortfall the work-back logic can never cancel — it is a pay cut, not hours owed. Business trip and training count as WORKED, not as absence.' },
        { label: 'Remove', desc: 'Deletes the spell, its calendar days and any vacation booking. The audit record of it stays.' },
      ]} />
    </div>
  );
}

// ---------------------------------------------------------------------------------------------

function AbsenceDrawer({ absence, types, people, busy, onClose, onSaved, call }: {
  absence: Absence | null; types: Record<string, string>; people: Person[]; busy: boolean;
  onClose: () => void; onSaved: (msg: string) => void;
  call: (u: string, m: string, b?: any) => Promise<any>;
}) {
  const isNew = !absence;
  const today = new Date().toISOString().slice(0, 10);
  const [who, setWho] = useState(absence?.username || (people[0]?.username ?? ''));
  const [type, setType] = useState(absence?.type || 'vacation');
  const [from, setFrom] = useState(absence?.start_date || today);
  const [to, setTo] = useState(absence?.end_date || today);
  const [halfStart, setHalfStart] = useState(absence?.half_day_start || false);
  const [halfEnd, setHalfEnd] = useState(absence?.half_day_end || false);
  const [notified, setNotified] = useState<string>(
    absence?.notified === true ? 'yes' : absence?.notified === false ? 'no' : '');
  const [notifiedAt, setNotifiedAt] = useState(absence?.notified_at?.slice(0, 16).replace(' ', 'T') || '');
  const [via, setVia] = useState(absence?.notified_via || '');
  const [certRef, setCertRef] = useState(absence?.certificate_ref || '');
  const [note, setNote] = useState(absence?.note || '');

  const submit = async () => {
    const body: any = {
      username: who, type, start_date: from, end_date: to,
      half_day_start: halfStart, half_day_end: halfEnd,
      notified: notified === 'yes' ? true : notified === 'no' ? false : null,
      notified_at: notified === 'yes' && notifiedAt ? notifiedAt : null,
      notified_via: notified === 'yes' ? (via || null) : null,
      certificate_ref: certRef || null,
      note: note || null,
    };
    const out = isNew
      ? await call(`${API}/statistics/absences`, 'POST', body)
      : await call(`${API}/statistics/absences/${absence!.id}`, 'PUT', body);
    if (!out) return;
    const p = out.projection || {};
    const bits = [`${(p.applied || []).length} day(s) set`];
    if ((p.skipped || []).length) {
      bits.push(`skipped ${p.skipped.length}: ` +
        p.skipped.map((x: any) => `${x.date.slice(5)} (${x.reason})`).join(', '));
    }
    if (out.vacation_days_booked != null) bits.push(`${out.vacation_days_booked} vacation day(s) booked`);
    onSaved(bits.join(' · '));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 overflow-y-auto py-10"
         onClick={onClose}>
      <div className="bg-[#151822] border border-gray-800 rounded-2xl w-full max-w-lg p-5 space-y-3"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <h3 className="text-white font-semibold">{isNew ? 'Record an absence' : 'Edit absence'}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white text-lg leading-none">×</button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs text-gray-500">Person</span>
            <select className={`${inp} w-full mt-1`} value={who} onChange={e => setWho(e.target.value)}
              disabled={!isNew}>
              {people.map(p => <option key={p.username} value={p.username}>{p.display_name}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs text-gray-500">Type</span>
            <select className={`${inp} w-full mt-1`} value={type} onChange={e => setType(e.target.value)}>
              {Object.entries(types).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs text-gray-500">First day</span>
            <input type="date" className={`${inp} w-full mt-1`} value={from} onChange={e => setFrom(e.target.value)} />
            <label className="flex items-center gap-2 text-[11px] text-gray-500 mt-1">
              <input type="checkbox" checked={halfStart} onChange={e => setHalfStart(e.target.checked)} />
              half day
            </label>
          </label>
          <label className="block">
            <span className="text-xs text-gray-500">Last day</span>
            <input type="date" className={`${inp} w-full mt-1`} value={to} onChange={e => setTo(e.target.value)} />
            <label className="flex items-center gap-2 text-[11px] text-gray-500 mt-1">
              <input type="checkbox" checked={halfEnd} onChange={e => setHalfEnd(e.target.checked)} />
              half day
            </label>
          </label>
        </div>

        <div className="border-t border-gray-800 pt-3">
          <span className="text-xs text-gray-500">Did they let us know?</span>
          <div className="flex gap-1 bg-gray-900/60 rounded-lg p-1 mt-1">
            {([['yes', 'Yes'], ['no', 'No — no-show'], ['', 'Not recorded']] as const).map(([v, l]) => (
              <button key={v} onClick={() => setNotified(v)}
                className={`flex-1 text-xs py-1.5 rounded ${notified === v ? 'bg-blue-600 text-white' : 'text-gray-400'}`}>
                {l}
              </button>
            ))}
          </div>
          {notified === 'yes' && (
            <div className="grid grid-cols-2 gap-3 mt-2">
              <label className="block">
                <span className="text-xs text-gray-500">When they told us</span>
                <input type="datetime-local" className={`${inp} w-full mt-1`} value={notifiedAt}
                  onChange={e => setNotifiedAt(e.target.value)} />
              </label>
              <label className="block">
                <span className="text-xs text-gray-500">How</span>
                <select className={`${inp} w-full mt-1`} value={via} onChange={e => setVia(e.target.value)}>
                  {VIA.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </label>
            </div>
          )}
          {notified === 'yes' && (
            <p className="text-[11px] text-gray-600 mt-1">
              Notice is worked out from this against the first day — a time after the shift started
              records as late notice.
            </p>
          )}
          {notified === 'no' && (
            <p className="text-[11px] text-amber-500/80 mt-1">
              Recorded as a no-show, and listed under “No-shows only”.
            </p>
          )}
        </div>

        <label className="block">
          <span className="text-xs text-gray-500">Certificate reference (optional)</span>
          <input className={`${inp} w-full mt-1`} value={certRef} onChange={e => setCertRef(e.target.value)}
            placeholder="e.g. HL-2026-0812 — sick leave only" />
        </label>
        <label className="block">
          <span className="text-xs text-gray-500">Note</span>
          <input className={`${inp} w-full mt-1`} value={note} onChange={e => setNote(e.target.value)} />
        </label>

        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className={`${btn} bg-gray-800 hover:bg-gray-700 text-gray-300`}>Cancel</button>
          <button onClick={submit} disabled={busy}
            className={`${btn} bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white`}>
            {busy ? 'Saving…' : isNew ? 'Record absence' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
